import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCreateDispute } from '@/hooks/useDisputes';
import { DISPUTE_REASON_TYPES } from '@sharecost/shared';
import type { DisputeReasonType } from '@sharecost/shared';
import {
  X,
  Loader2,
  UserX,
  Calculator,
  PieChart,
  Copy,
  MoreHorizontal,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface RaiseDisputeDialogProps {
  open: boolean;
  onClose: () => void;
  expenseId: string;
  groupId: string;
  expenseDescription: string;
}

const REASON_ICONS: Record<DisputeReasonType, React.ElementType> = {
  not_present: UserX,
  wrong_amount: Calculator,
  wrong_split: PieChart,
  duplicate: Copy,
  other: MoreHorizontal,
};

export function RaiseDisputeDialog({
  open,
  onClose,
  expenseId,
  groupId,
  expenseDescription,
}: RaiseDisputeDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const createDispute = useCreateDispute();
  const [reasonType, setReasonType] = useState<DisputeReasonType | null>(null);
  const [reasonText, setReasonText] = useState('');

  if (!open) return null;

  const handleClose = () => {
    setReasonType(null);
    setReasonText('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reasonType || !user) return;
    try {
      await createDispute.mutateAsync({
        expenseId,
        groupId,
        userId: user.id,
        reasonType,
        reasonText: reasonText.trim() || undefined,
      });
      handleClose();
    } catch {
      // Error available via createDispute.error
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={handleClose}>
      <div
        className="w-full max-w-md max-h-full overflow-y-auto rounded-xl bg-surface-primary p-6 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">{t('onboarding:tutorial.expense-disputes.slide1.title')}</h2>
          <button onClick={handleClose} className="rounded-md p-1 text-text-quaternary hover:text-text-secondary">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="mb-4 rounded-lg bg-surface-secondary px-3 py-2 text-sm text-text-secondary">
          {t('web:raiseDisputeDialog.disputing')} <span className="font-medium text-text-primary">{expenseDescription}</span>
        </p>

        {createDispute.error && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
            {(createDispute.error as any)?.message ?? t('web:raiseDisputeDialog.failedToCreateDispute')}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t('web:raiseDisputeDialog.whatSWrongWithThisExpense')}
            </label>
            <div className="space-y-2">
              {DISPUTE_REASON_TYPES.map(
                (type) => {
                  const label = t(`expenses:dispute.reason.${type}`);
                  const Icon = REASON_ICONS[type];
                  const isSelected = reasonType === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setReasonType(type)}
                      className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                        isSelected
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300'
                          : 'border-border-secondary text-text-secondary hover:bg-surface-hover'
                      }`}
                    >
                      <Icon className={`h-4 w-4 shrink-0 ${isSelected ? 'text-brand-600' : 'text-text-quaternary'}`} />
                      {label}
                    </button>
                  );
                },
              )}
            </div>
          </div>

          <div>
            <label htmlFor="dispute-reason" className="block text-sm font-medium text-text-secondary mb-1">
              {t('web:raiseDisputeDialog.additionalContext')} <span className="text-text-quaternary">{t('web:common.optional')}</span>
            </label>
            <textarea
              id="dispute-reason"
              value={reasonText}
              onChange={e => setReasonText(e.target.value)}
              placeholder={t('web:raiseDisputeDialog.explainWhyYouReDisputingThis')}
              maxLength={1000}
              rows={3}
              className="w-full rounded-lg border border-border-secondary px-3 py-2 text-sm placeholder:text-text-quaternary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 resize-none"
            />
            {reasonText.length > 0 && (
              <p className="mt-1 text-xs text-text-quaternary text-right">{reasonText.length}/1000</p>
            )}
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 rounded-lg border border-border-secondary px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-surface-hover"
            >
              {t('common:button.cancel')}
            </button>
            <button
              type="submit"
              disabled={!reasonType || createDispute.isPending}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {createDispute.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('expenses:dispute.raisedDispute')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
